import md5 from 'crypto-js/md5';
import * as actions from '@redux-actions/';

import {
    EncryptOnlineOrderIdPayload,
    GlobalOrderDetailsPayload,
    IOnlineOrderItems,
    OnlineOrderDetailsSkuDetails,
    OnlinerOrderHistoryPayload,
    OrderItemCarrierUrl,
} from '@interfaces/myPurchasesInterfaces';

import {
    mapContactLensesAttributes,
    mapGlobalOrderDetails,
    mapGlobalOrderDetailsImages,
    mapOnlineOrdersHistory,
    mapRetailOrderHistory,
} from '@utils/pages-utils/MyPurchasesUtils';
import URLS from '@utils/urlList';
import { getMockedFile, getThreeYearsBack } from '@utils/utils';

import axios, { AxiosRequestConfig } from 'axios';
import { map } from 'lodash';

export const getEncryptedOnlineOrderId = (payload: EncryptOnlineOrderIdPayload) => {
    const { isMockRequest, storeId, ...rest } = payload;
    if (isMockRequest) {
        const encryptedOnlineOrderId =
            '/data/mocks/my_purchases_get_encrypted_online_order_id_mock.json';
        return dispatch => {
            dispatch(actions.getEncryptedOnlineOrderIdPending());
            getMockedFile(encryptedOnlineOrderId)
                .then(result => {
                    dispatch(actions.getEncryptedOnlineOrderIdSuccess(result?.data?.encOrderId));
                })
                .catch(() => dispatch(actions.getEncryptedOnlineOrderIdError()));
        };
    } else {
        const encryptedOnlineOrderIdUrl = URLS.GET_ENCRYPTED_ONLINE_ORDER_ID.replace(
            '{storeId}',
            storeId
        );

        const config: AxiosRequestConfig = {
            method: 'POST',
            url: encryptedOnlineOrderIdUrl,
            headers: { 'Content-Type': 'application/json' },
            data: rest,
        };

        return dispatch => {
            dispatch(actions.getEncryptedOnlineOrderIdPending());
            axios(config)
                .then(result => {
                    dispatch(actions.getEncryptedOnlineOrderIdSuccess(result?.data?.encOrderId));
                })
                .catch(() => dispatch(actions.getEncryptedOnlineOrderIdError()));
        };
    }
};

export const getPrescriptionInfo = (isMockRequest, storeId: string, orderId: string, userId: string) => {
    if (isMockRequest) {
        const prescriptionInfoMock = '/data/mocks/online_orders_history_prescription_mock.json';
        return dispatch => {
            getMockedFile(prescriptionInfoMock)
                .then(result => {
                    dispatch(actions.getPrescriptionInfoDetails(result.data.response.data.dataContent.rxDataContent));
                })
                .catch(() => {
                    console.log('Error on Get Prescription Info for: ' + orderId);
                });
        };
    } else {
        var getPrescriptionInfoUrl = URLS.GET_PRESCRIPTION_INFO.replace('{storeId}', storeId).replace('{orderId}', orderId);
        const config: AxiosRequestConfig = {
            method: 'GET',
            url: getPrescriptionInfoUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            axios(config)
                .then(result => {
                    dispatch(actions.getPrescriptionInfoDetails(result.data.response.data.dataContent.rxDataContent));
                })
                .catch(() => {
                    console.log('Error on Get Prescription Info for: ' + orderId);
                });
        };
    }
};

export const getOnlineOrdersHistory = (payload: OnlinerOrderHistoryPayload) => {
    const { isMockRequest, storeId, pageNumber, pageSize, currency } = payload;
    if (isMockRequest) {
        const onlineOrdersHistory = '/data/mocks/online_orders_history_mock.json';
        return dispatch => {
            dispatch(actions.getOnlineOrdersHistoryPending());
            getMockedFile(onlineOrdersHistory)
                .then(result => {
                    const response = mapOnlineOrdersHistory(result?.data?.Order);
                    dispatch(actions.getOnlineOrdersHistorySuccess(response));
                })
                .catch(() => {
                    dispatch(actions.getOnlineOrdersHistoryError());
                });
        };
    } else {
        const onlineOrdersHistoryUrl = URLS.GET_ONLINE_ORDERS_HISTORY.replace('{storeId}', storeId)
            .replace('{currency}', currency)
            .replace('{pageNumber}', pageNumber.toString())
            .replace('{pageSize}', pageSize.toString())
            .replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: onlineOrdersHistoryUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getOnlineOrdersHistoryPending());
            axios(config)
                .then(result => {
                    if (!!result.data.Order) {
                        const dateThreeYearsBack = getThreeYearsBack() + 'T00:00:00Z';
                        const testData = result?.data?.Order.filter(item => {
                            const referenceDate = new Date(dateThreeYearsBack);
                            const orderDate = new Date(item.lastUpdateDate);

                            return orderDate > referenceDate;
                        })
                        const response = mapOnlineOrdersHistory(testData);
                        dispatch(actions.getOnlineOrdersHistorySuccess(response));
                    } else {
                        const response = mapOnlineOrdersHistory(result?.data?.Order);
                        dispatch(actions.getOnlineOrdersHistorySuccess(response));
                    }
                })
                .catch(() => {
                    dispatch(actions.getOnlineOrdersHistoryError());
                });
        };
    }
};

export const getRetailOrderHistory = (isMockRequest: boolean, storeId: string, emailAccount: string) => {
    const dateThreeYearsBack = getThreeYearsBack()

    const payload = {
        banner: "TO",
        country: "US",
        email: emailAccount,
        order_history_type: "RETAIL_ONLY",
        start_date: dateThreeYearsBack,
        language_code: "en_US",
        sort: "RECENT_FIRST",
    }

    const retailOrderHistory = URLS.GET_RETAIL_ORDERS.replace('{storeId}', storeId).replace('{noCache}', new Date().getTime().toString());

    const config: AxiosRequestConfig = {
        method: 'POST',
        url: retailOrderHistory,
        headers: { 'Content-Type': 'application/json' },
        data: payload
    };

    return dispatch => {
        dispatch(actions.getRetailOrderHistoryPending());

        axios(config)
            .then(retailResult => {
                const response = mapRetailOrderHistory(retailResult?.data?.response);
                dispatch(actions.getRetailOrderHistorySuccess(response.slice(0, 50)));
            })
            .catch(() => {
                dispatch(actions.getRetailOrderHistoryError());
            });
    };
}

export const getPrescriptionInfoForOrders = (isMockRequest: boolean, storeId: string, emailAccount: string) => {
    let payload = {
        banner: "TO",
        country: "US",
        email: emailAccount,
        channel_filter: "RETAIL_ONLY",
        language_code: "en_US",
        keepCancelled: true
    }
    const getPrescriptionInfo = URLS.GET_PRESCRIPTION_RETAIL_ORDERS.replace('{storeId}', storeId).replace('{noCache}', new Date().getTime().toString());

    const config: AxiosRequestConfig = {
        method: 'POST',
        url: getPrescriptionInfo,
        headers: { 'Content-Type': 'application/json' },
        data: payload
    };

    return new Promise((resolve, reject) => {
        axios(config)
            .then(result => {
                const mappedResult = result?.data;
                resolve(mappedResult);
            })
            .catch((err: any) => {
                reject(err.response);
            });
    })
}

export const getOrderItemCarrierUrl = (payload: OrderItemCarrierUrl) => {
    const { isMockRequest, ...rest } = payload;

    if (isMockRequest) {
        const getOrderItemCarrierUrl = '/data/mocks/my_purchases_get_carrier_url_mock.json';
        return dispatch => {
            dispatch(actions.getOrderItemCarrierUrlPending());
            getMockedFile(getOrderItemCarrierUrl)
                .then(result => {
                    dispatch(actions.getOrderItemCarrierUrlSuccess(result?.data?.carrierUrl));
                })
                .catch(() => dispatch(actions.getOrderItemCarrierUrlError()));
        };
    } else {
        const getOrderItemCarrierUrl = URLS.GET_ORDER_ITEM_CARRIER_URL.replace(
            '{storeId}',
            rest.storeId
        ).replace('{orderItemId}', rest.orderItemId)
            .replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: getOrderItemCarrierUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getOrderItemCarrierUrlPending());
            axios(config)
                .then(result => {
                    dispatch(actions.getOrderItemCarrierUrlSuccess(result?.data?.carrierUrl));
                })
                .catch(() => dispatch(actions.getOrderItemCarrierUrlError()));
        };
    }
};

export const getGlobalOrderDetails = (payload: GlobalOrderDetailsPayload) => {
    const { isMockRequest, ...rest } = payload;
    if (isMockRequest) {
        const globalOrderDetails = '/data/mocks/my_purchases_get_online_order_details_mock.json';
        return dispatch => {
            dispatch(actions.getGlobalOrderDetailsPending());
            getMockedFile(globalOrderDetails)
                .then(result => {
                    const mappedResult = mapGlobalOrderDetails(result?.data);
                    dispatch(actions.getGlobalOrderDetailsSuccess(mappedResult));
                })
                .catch(() => {
                    dispatch(actions.getGlobalOrderDetailsError());
                });
        };
    } else {
        const globalOrderDetailsUrl = URLS.GET_GLOBAL_ORDER_DETAILS.replace(
            '{storeId}',
            rest.storeId.toString()
        )
            .replace('{orderId}', rest.orderId.toString())
            .replace('{currency}', rest.currency)
            .replace('{pageNumber}', rest.pageNumber.toString())
            .replace('{pageSize}', rest.pageSize.toString())
            .replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: globalOrderDetailsUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getGlobalOrderDetailsPending());
            axios(config)
                .then(result => {
                    const mappedResult = mapGlobalOrderDetails(result?.data);
                    dispatch(actions.getGlobalOrderDetailsSuccess(mappedResult));
                })
                .catch(() => {
                    dispatch(actions.getGlobalOrderDetailsError());
                });
        };
    }
};

export const getMyPurchases = (isMockRequest: boolean) => {
    const myPurchases =
        isMockRequest && process.env.NODE_ENV === 'development'
            ? '/data/mocks/in_store_purchases_my_purchases_mock.json'
            : 'call the service';
    return dispatch => {
        dispatch(actions.getMyPurchasesPending());
        getMockedFile(myPurchases)
            .then(result => {
                dispatch(actions.getMyPurchasesSuccess(result?.data?.Purchases));
            })
            .catch(() => {
                dispatch(actions.getMyPurchasesError());
            });
    };
};

export const getTrackThisItemInfo = (isMockRequest, storeId: string, orderId: string, email: string, lastName: string) => {
    var trackYourOrderByEmail = URLS.TRACK_YOUR_ORDER_BY_ORDERID_AND_EMAIL.replace('{storeId}', storeId).replace('{orderId}', orderId).replace('{email}', email);
    var trackYourOrderByLastName = URLS.TRACK_YOUR_ORDER_BY_ORDERID_AND_LASTNAME.replace('{storeId}', storeId).replace('{orderId}', orderId).replace('{lastName}', lastName);
    var redirectByEmail = URLS.TRACK_YOUR_ORDER_REDIRECT_EMAIL.replace('{orderId}', orderId).replace('{email}', email);
    var redirectByLastName = URLS.TRACK_YOUR_ORDER_REDIRECT_LASTNAME.replace('{orderId}', orderId).replace('{lastName}', lastName);

    const configEmail: AxiosRequestConfig = {
        method: 'GET',
        url: trackYourOrderByEmail,
        headers: { 'Content-Type': 'application/json' },
    };

    const configLastName: AxiosRequestConfig = {
        method: 'GET',
        url: trackYourOrderByLastName,
        headers: { 'Content-Type': 'application/json' },
    };

    return dispatch => {
        axios(configEmail)
            .then(result => {
                console.log("TEST RESULT: " + Object.keys(result.data).length)
                if (Object.keys(result.data).length > 0) {
                    console.log("Sono entrato in redirect email " + redirectByEmail)
                    window.location.href = redirectByEmail
                } else {
                    axios(configLastName)
                        .then(result => {
                            console.log("Sono entrato in redirect last name " + redirectByLastName)
                            window.location.href = redirectByLastName
                        })
                        .catch(() => {
                            console.log('Error on Track your item for: ' + orderId)
                        })
                }
            })
            .catch(() => {
                console.log('Error on Get Prescription Info for: ' + orderId);
            });
    };
};
